import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { organizationApi } from "@/resources/organization";
import { handleApiError, formatRelativeTime } from "@/utils";
import { Organization } from "@/types";

export function OrganizationsTab() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [orgName, setOrgName] = useState("");
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);

  const { data, isLoading } = organizationApi.useGetOrganizations({ search: search || undefined, page });
  const { mutate: createOrg, isPending: isCreating } = organizationApi.useCreateOrganization();
  const { mutate: updateOrg, isPending: isUpdating } = organizationApi.useUpdateOrganization();
  const { mutate: deleteOrg, isPending: isDeleting } = organizationApi.useDeleteOrganization();

  const organizations = data?.data ?? [];
  const lastPage = data?.last_page ?? 1;
  const total = data?.total ?? 0;

  const openCreateDialog = () => {
    setEditingOrg(null);
    setOrgName("");
    setDialogOpen(true);
  };

  const openEditDialog = (org: Organization) => {
    setEditingOrg(org);
    setOrgName(org.name);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!orgName.trim()) return;
    if (editingOrg) {
      updateOrg(
        { id: editingOrg.id, data: { name: orgName.trim() } },
        {
          onSuccess: () => {
            toast.success("Organization updated");
            setDialogOpen(false);
          },
          onError: (error) => handleApiError(error),
        }
      );
    } else {
      createOrg(
        { name: orgName.trim() },
        {
          onSuccess: () => {
            toast.success("Organization created");
            setDialogOpen(false);
          },
          onError: (error) => handleApiError(error),
        }
      );
    }
  };

  const handleDelete = () => {
    if (!orgToDelete) return;
    deleteOrg(orgToDelete.id, {
      onSuccess: () => {
        toast.success("Organization deleted", { description: `"${orgToDelete.name}" has been removed.` });
        setOrgToDelete(null);
      },
      onError: (error) => handleApiError(error),
    });
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Organizations</CardTitle>
              <CardDescription>Manage all organizations in the platform ({total} total).</CardDescription>
            </div>
            <Button onClick={openCreateDialog} size="sm" className="rounded-lg">
              <Plus className="h-4 w-4 mr-1.5" />
              Add Organization
            </Button>
          </div>
          <ClearableInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search organizations..."
            className="h-9 rounded-lg bg-card"
            wrapperClassName="mt-3"
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : organizations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {search ? "No organizations match your search." : "No organizations yet."}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-[100px]">Users</TableHead>
                    <TableHead className="w-[160px]">Created</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell className="font-medium">{org.name}</TableCell>
                      <TableCell>{org.users_count}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatRelativeTime(org.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditDialog(org)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setOrgToDelete(org)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {lastPage > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {lastPage}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= lastPage}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-card">
          <DialogHeader>
            <DialogTitle>{editingOrg ? "Edit Organization" : "Add Organization"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Organization Name</Label>
              <Input
                placeholder="Enter organization name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                className="h-9 rounded-lg bg-card"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!orgName.trim() || isCreating || isUpdating}
              className="rounded-lg"
            >
              {(isCreating || isUpdating) && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {editingOrg ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDeleteDialog
        open={!!orgToDelete}
        onOpenChange={(open) => !open && setOrgToDelete(null)}
        title="Delete Organization"
        description={<>Are you sure you want to delete <strong>{orgToDelete?.name}</strong>? This will remove all associated users, brands, and data.</>}
        onConfirm={handleDelete}
        isPending={isDeleting}
      />
    </>
  );
}
